import axios from 'axios';

// ─────────────────────────────────────────────
// BASE CONFIGURATION
// ─────────────────────────────────────────────
const BASE_URL = 'https://goprojectflow.com/wp-json';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gpf_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────
export const login = async (username, password) => {
  const response = await api.post('/jwt-auth/v1/token', { username, password });
  const { token, user_display_name, user_email, user_id } = response.data;
  localStorage.setItem('gpf_token', token);
  localStorage.setItem('gpf_user', JSON.stringify({ name: user_display_name, email: user_email, id: user_id }));
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('gpf_token');
  localStorage.removeItem('gpf_user');
};

export const getCurrentUser = () => {
  const user = localStorage.getItem('gpf_user');
  return user ? JSON.parse(user) : null;
};

export const isLoggedIn = () => !!localStorage.getItem('gpf_token');

// ─────────────────────────────────────────────
// PROJECTS
// ─────────────────────────────────────────────
export const reorderItems = (postType, orderedIds) =>
  api.post('/gpf/v1/reorder', { post_type: postType, ordered_ids: orderedIds });

export const getProjects = () =>
  api.get('/wp/v2/projects?per_page=100');

export const getMyCompanies = () =>
  api.get('/gpf/v1/companies/mine');

export const createCompany = (data) =>
  api.post('/wp/v2/companies', { title: data.name, status: 'publish', meta: data.meta || {} });

export const updateCompany = (id, data) =>
  api.post(`/wp/v2/companies/${id}`, { title: data.name, meta: data.meta || {} });

export const deleteCompany = (id) =>
  api.delete(`/wp/v2/companies/${id}?force=true`);

export const getProject = (id) =>
  api.get(`/wp/v2/projects/${id}`);

export const createProject = (data) =>
  api.post('/wp/v2/projects', {
    title:  data.title,
    content: data.description,
    status: 'publish',
    meta: {
      gpf_status:         data.status     || 'active',
      gpf_priority:       data.priority   || 'medium',
      gpf_progress:       data.progress   || 0,
      gpf_start_date:     data.start_date || '',
      gpf_end_date:       data.end_date   || '',
      gpf_owner_id:       data.owner_id   || 0,
      gpf_assigned_users: JSON.stringify(data.assigned_users || []),
      gpf_department:     data.department    || '',
      gpf_company_id:     data.company_id    || 0,
    },
  });

export const updateProject = (id, data) =>
  api.put(`/wp/v2/projects/${id}`, {
    title:   data.title,
    content: data.description,
    meta: {
      gpf_status:         data.status,
      gpf_priority:       data.priority,
      gpf_progress:       data.progress,
      gpf_start_date:     data.start_date,
      gpf_end_date:       data.end_date,
      gpf_owner_id:       data.owner_id,
      gpf_assigned_users: JSON.stringify(data.assigned_users || []),
      gpf_started_by:     data.started_by   || '',
      gpf_started_date:   data.started_date || '',
      gpf_department:     data.department    || '',
    },
  });

export const deleteProject = (id) =>
  api.delete(`/wp/v2/projects/${id}?force=true`);

// ─────────────────────────────────────────────
// TASKS
// ─────────────────────────────────────────────
export const getTasks = (projectId = null) => {
  const url = projectId
    ? `/wp/v2/tasks?per_page=100&project_id=${projectId}`
    : '/wp/v2/tasks?per_page=100';
  return api.get(url);
};

export const getTask = (id) =>
  api.get(`/wp/v2/tasks/${id}`);

export const createTask = (data) =>
  api.post('/wp/v2/tasks', {
    title:   data.title,
    content: data.description,
    status:  'publish',
    meta: {
      gpf_project_id:     data.project_id  || 0,
      gpf_status:         data.status      || 'not_started',
      gpf_priority:       data.priority    || 'medium',
      gpf_progress:       data.progress    || 0,
      gpf_start_date:     data.start_date  || '',
      gpf_end_date:       data.end_date    || '',
      gpf_owner_id:       data.owner_id    || 0,
      gpf_hours:          data.hours       || 0,
      gpf_assigned_users: JSON.stringify(data.assigned_users || []),
    },
  });

export const updateTask = (id, data) =>
  api.put(`/wp/v2/tasks/${id}`, {
    title:   data.title,
    content: data.description,
    meta: {
      gpf_project_id:     data.project_id,
      gpf_status:         data.status,
      gpf_priority:       data.priority,
      gpf_progress:       data.progress,
      gpf_start_date:     data.start_date,
      gpf_end_date:       data.end_date,
      gpf_owner_id:       data.owner_id,
      gpf_hours:          data.hours,
      gpf_assigned_users: JSON.stringify(data.assigned_users || []),
      gpf_started_by:   data.started_by   || '',
      gpf_started_date: data.started_date || '',
    },
  });

export const deleteTask = (id) =>
  api.delete(`/wp/v2/tasks/${id}?force=true`);

// ─────────────────────────────────────────────
// NOTES
// ─────────────────────────────────────────────
export const getNotes = (taskId = null, projectId = null) => {
  if (taskId)    return api.get(`/wp/v2/notes?per_page=100&task_id=${taskId}`);
  if (projectId) return api.get(`/wp/v2/notes?per_page=100&project_id=${projectId}`);
  return api.get('/wp/v2/notes?per_page=100');
};

export const createNote = (data) =>
  api.post('/wp/v2/notes', {
    title:   data.title || 'Note',
    content: data.content,
    status:  'publish',
    meta: {
      gpf_task_id:    data.task_id    || 0,
      gpf_project_id: data.project_id || 0,
      gpf_author_id:  data.author_id  || 0,
      gpf_hours:      data.hours      || 0,
      gpf_date:       data.date       || new Date().toISOString().split('T')[0],
    },
  });

export const deleteNote = (id) =>
  api.delete(`/wp/v2/notes/${id}?force=true`);

// ─────────────────────────────────────────────
// USERS
// ─────────────────────────────────────────────
export const getUsers = () =>
  api.get('/wp/v2/users?per_page=100&context=edit&has_published_posts=false');

// ─────────────────────────────────────────────
// DEPARTMENTS + AI CLASSIFICATION
// ─────────────────────────────────────────────
export const getDepartments = () =>
  api.get('/gpf/v1/departments');

export const saveDepartments = (departments) =>
  api.post('/gpf/v1/departments', { departments });

export const classifyProject = (title, description) =>
  api.post('/gpf/v1/classify-project', { title, description });

// ─────────────────────────────────────────────
// AI AGENT
// ─────────────────────────────────────────────
export const analyzeTask = (taskData) =>
  api.post('/gpf/v1/ai-agent/analyze', taskData);

export const chatWithAgent = (question, context) =>
  api.post('/gpf/v1/ai-agent/chat', { question, context });

// ─────────────────────────────────────────────
// MEETINGS
// ─────────────────────────────────────────────
export const getMeetings = () =>
  api.get('/wp/v2/meetings?per_page=100&orderby=date&order=desc');

export const createMeeting = (data) =>
  api.post('/wp/v2/meetings', {
    title:   data.title,
    content: data.description || '',
    status:  'publish',
    meta: {
      gpf_date:         data.date         || '',
      gpf_time:         data.time         || '',
      gpf_duration:     data.duration     || 60,
      gpf_location:     data.location     || '',
      gpf_project_id:   data.project_id   || 0,
      gpf_organizer_id: data.organizer_id || 0,
      gpf_task_id:      data.task_id      || 0,
      gpf_invitees:     JSON.stringify(data.invitees || []),
    },
  });

export const updateMeeting = (id, data) =>
  api.put(`/wp/v2/meetings/${id}`, {
    title:   data.title,
    content: data.description || '',
    meta: {
      gpf_date:         data.date         || '',
      gpf_time:         data.time         || '',
      gpf_duration:     data.duration     || 60,
      gpf_location:     data.location     || '',
      gpf_project_id:   data.project_id   || 0,
      gpf_task_id:      data.task_id      || 0,
      gpf_organizer_id: data.organizer_id || 0,
      gpf_invitees:     JSON.stringify(data.invitees || []),
    },
  });

export const deleteMeeting = (id) =>
  api.delete(`/wp/v2/meetings/${id}?force=true`);

// ─────────────────────────────────────────────
// USER PROFILE
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// DOCUMENTS
// ─────────────────────────────────────────────
export const getDocuments = () =>
  api.get('/wp/v2/documents?per_page=100&orderby=modified&order=desc');

export const getDocument = (id) =>
  api.get(`/wp/v2/documents/${id}`);

export const createDocument = (data) =>
  api.post('/wp/v2/documents', {
    title:   data.title,
    content: data.content,
    status:  'publish',
    meta: {
      gpf_project_id:    data.project_id    || 0,
      gpf_task_id:       data.task_id       || 0,
      gpf_author_id:     data.author_id     || 0,
      gpf_last_modified: new Date().toISOString(),
    },
  });

export const updateDocument = (id, data) =>
  api.put(`/wp/v2/documents/${id}`, {
    title:   data.title,
    content: data.content,
    meta: {
      gpf_project_id:    data.project_id    || 0,
      gpf_task_id:       data.task_id       || 0,
      gpf_last_modified: new Date().toISOString(),
    },
  });

export const deleteDocument = (id) =>
  api.delete(`/wp/v2/documents/${id}?force=true`);

export const updateProfile = (data) =>
  api.post('/gpf/v1/profile', data);

export const uploadProfileImage = (formData) =>
  api.post('/gpf/v1/profile/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const saveTimezone = (timezone) =>
  api.post('/gpf/v1/timezone', { timezone });

export const getSettings = () =>
  api.get('/gpf/v1/settings');

export const saveSettings = (data) =>
  api.post('/gpf/v1/settings', data);

export default api;